import { AppState } from '@chegg-wt/core/state'
import RouterPagination from '@chegg-wt/core/components/RouterPagination'
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch, connect } from 'react-redux'
import { AppLink } from '@chegg-wt/core/components/AppLink'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import {
  COPY_SUCCESS_INTERSTITIAL_MODAL,
  COPY_SUCCESS_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import PremiumCopyModal from '@chegg-wt/core/components/Modals/PremiumCopyModal'
import { useRouter } from 'next/router'
import { useQuery } from '@apollo/client'

import { CITATION_COUNT } from '@chegg-wt/core/queries/Citations'
import {
  getCitationCount,
  getMaxCitationPages,
  getShowPagination,
} from '@chegg-wt/core/utils/citations'
import { copyAll } from '@chegg-wt/core/utils/copy'
import { Citation as CitationAPICitation } from '@chegg-wt/core/types/redux-citation-api'
import { Project } from '@chegg-wt/core/types/core'
import {
  fetchCitations,
  setCitationPage,
} from '@chegg-wt/core/state/bibliography/actions'
import { onNewCitation } from '@chegg-wt/core/state/createCitation/actions'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { initializedSelector } from '@chegg-wt/core/state/app/selectors'
import {
  isCitationsFetchingSelector,
  completedCitationsSelector,
} from '@chegg-wt/core/state/bibliography/selectors'
import { shouldShowValidatorWarningSelector } from '@chegg-wt/core/state/citation/selectors'
import {
  currentProjectSelector,
  currentStyleSelector,
  currentProjectIdSelector,
  currentProjectIsLoadingSelector,
  footnoteStyleSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import { adConfigVersionSelector } from '@chegg-wt/core/state/featureFlags/selectors'
import { userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { UserRole } from '@chegg-wt/core/constants/user'
import { Config } from '@chegg-wt/core/config'
import * as Styled from './styled'
import CitationListHeader from './CitationListHeader'
import Citation from './Citation'
import { getMenuId } from './Citation/Menu'

interface StateProps {
  shouldShowWarning: boolean
  isFootnote: boolean
  currentStyle: string
  currentProjectId: string
  projectData: Project
  adConfigVersion: string
  userRole: UserRole
}

interface DispatchProps {
  openModal: (payload: OpenModalPayload) => void
  fetchCitations: (projectId?: string) => void
  setCitationPage: (page: number) => void
}

type Props = StateProps & DispatchProps

export const CitationList: React.FC<Props> = ({
  shouldShowWarning,
  isFootnote,
  currentStyle,
  currentProjectId,
  projectData,
  userRole,
  openModal,
  fetchCitations,
  setCitationPage,
}) => {
  // selectors
  const isCitationLoading = useSelector(isCitationsFetchingSelector)
  const citations = useSelector(completedCitationsSelector)
  const citationStyle = useSelector(currentStyleSelector)
  const isProjectLoading = useSelector(currentProjectIsLoadingSelector)
  const initialized = useSelector(initializedSelector)
  // dispatch
  const dispatch = useDispatch()
  // next.js router
  const router = useRouter()
  // get the link url
  const { toSourceTypeSelection } = useRoutesContext()
  const { page: pageQuery, id: projectId } = router.query as {
    page: string
    id: string
  }
  const page = parseInt(pageQuery, 10) || 1

  useEffect(() => {
    if (initialized) setCitationPage(page)
  }, [initialized, page, setCitationPage])

  const [
    premiumCopyCitation,
    setPremiumCopyCitation,
  ] = useState<CitationAPICitation | null>(null)

  const copyCitation = (citation: CitationAPICitation) => {
    copyAll([citation])
    openModal({
      name:
        userRole !== 'mybibpro' && Config.showInterstitialAd
          ? COPY_SUCCESS_INTERSTITIAL_MODAL
          : COPY_SUCCESS_MODAL,
      data: {
        text: 'Citation',
        shouldShowWarning,
        returnFocusSelector: `#${getMenuId(citation.id)}`,
      },
    })
  }

  const copyPremiumCitation = (citation: CitationAPICitation) => {
    setPremiumCopyCitation(citation)
  }

  const fetchPageCitations = (page: number) => {
    setCitationPage(page)
    fetchCitations()
  }

  const { data: citationsCountData } = useQuery(CITATION_COUNT, {
    fetchPolicy: 'cache-and-network',
    variables: {
      projectId: projectId || currentProjectId,
    },
    ssr: false,
    skip:
      !initialized || !projectData || !currentStyle || !(projectId || currentProjectId),
  })

  const citationCount = getCitationCount(citationsCountData)
  const showPagination = getShowPagination(citationCount)
  const maxPages = getMaxCitationPages(citationCount)

  if (isCitationLoading || isProjectLoading) return <p>Loading..</p>
  return (
    <>
      {citationCount ? (
        <>
          <CitationListHeader enableSort={!showPagination} />
          {citations.map((citation) => (
            <Citation
              citation={citation}
              key={citation.id}
              copyCitation={copyCitation}
              copyPremiumCitation={copyPremiumCitation}
            />
          ))}
          {showPagination && (
            <RouterPagination
              page={page}
              setPage={fetchPageCitations}
              maxPages={maxPages}
            />
          )}
        </>
      ) : (
        <Styled.EmptyList>
          <Styled.Text>You don&apos;t have any citations on this list.</Styled.Text>
          <AppLink route={toSourceTypeSelection({ style: citationStyle })}>
            <Styled.Button
              data-test="empty-bib-link"
              onClick={() => {
                dispatch(onNewCitation())
              }}
            >
              Create a new citation
            </Styled.Button>
          </AppLink>
        </Styled.EmptyList>
      )}
      {premiumCopyCitation && (
        <PremiumCopyModal
          citation={premiumCopyCitation}
          isFootnote={isFootnote}
          currentStyle={citationStyle}
          onClose={() => setPremiumCopyCitation(null)}
        />
      )}
    </>
  )
}

const mapStateToProps = (state: AppState) => ({
  shouldShowWarning: shouldShowValidatorWarningSelector(state),
  isFootnote: footnoteStyleSelector(state),
  currentStyle: currentStyleSelector(state),
  currentProjectId: currentProjectIdSelector(state),
  projectData: currentProjectSelector(state),
  adConfigVersion: adConfigVersionSelector(state),
  userRole: userRoleSelector(state),
})

const mapDispatchToProps = {
  openModal,
  fetchCitations,
  setCitationPage,
}

export default connect(mapStateToProps, mapDispatchToProps)(CitationList)
